/**
 * Utilitário de Analytics para rastreamento híbrido (GTM Client-Side + Meta CAPI Server-Side)
 */

interface UserTrackingData {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  external_id?: string;
}

// Helper para normalizar e formatar telefone de acordo com o padrão Meta (somente números + DDI)
export function normalizePhone(phone: string | undefined): string | null {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 0) return null;
  
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }
  // Se tiver 10 ou 11 dígitos, está sem DDI (Brasil = 55)
  if (cleaned.length === 10 || cleaned.length === 11) {
    cleaned = '55' + cleaned;
  }
  return cleaned;
}

// Auxiliar para obter cookies primários da Meta
function getCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
  return '';
}

// Auxiliar robusto para obter parâmetros da URL (suporta hash router)
function getQueryParam(name: string): string {
  if (typeof window === 'undefined') return '';
  const searchParams = new URLSearchParams(window.location.search);
  let val = searchParams.get(name);
  if (val) return val;

  const hash = window.location.hash;
  const hashSearchIndex = hash.indexOf('?');
  if (hashSearchIndex !== -1) {
    const hashSearchParams = new URLSearchParams(hash.substring(hashSearchIndex + 1));
    val = hashSearchParams.get(name);
    if (val) return val;
  }
  return '';
}

// Auxiliar para obter a rota limpa do HashRouter
function getHashRoute(): string {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash || '';
  if (hash) {
    const cleanHash = hash.split('?')[0].replace('#', '');
    return cleanHash || '/';
  }
  return window.location.pathname || '/';
}

// Gerar um Event ID único para deduplicação entre Pixel e Conversions API
export function generateEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Dispara um evento de tracking híbrido (Pixel do Browser + API de Conversões)
 * 
 * @param eventName Nome do evento (ex: 'Lead', 'InitiateCheckout', 'Purchase', 'PageView')
 * @param userData Dados do usuário para correspondência avançada (email, telefone, nome)
 * @param customData Objeto de metadados adicionais do evento (ex: { value: 49.90, currency: 'BRL' })
 */
export async function trackEvent(
  eventName: string,
  userData?: UserTrackingData,
  customData?: Record<string, any>
) {
  const eventId = generateEventId();
  
  // Dividir dinamicamente o first_name em primeiro nome e sobrenome se contiver espaços e last_name estiver ausente
  let finalFirstName = userData?.first_name;
  let finalLastName = userData?.last_name;
  
  if (finalFirstName && !finalLastName) {
    const parts = finalFirstName.trim().split(/\s+/);
    if (parts.length > 1) {
      finalFirstName = parts[0];
      finalLastName = parts.slice(1).join(' ');
    }
  }

  const currentRoute = getHashRoute();

  // Só envia para a Meta se:
  // 1. For um PageView na Landing Page ('/') ou página de login/cadastro ('/login')
  // 2. Ou for um dos eventos de conversão e onboarding explícitos
  const isMetaPageViewAllowed = eventName === 'PageView' && ['/', '/login'].includes(currentRoute);
  const isMetaConversionEvent = ['Lead', 'inicio_cadastro', 'clique_cta', 'Subscribe', 'InitiateCheckout', 'Purchase'].includes(eventName);
  const shouldSendToMeta = isMetaPageViewAllowed || isMetaConversionEvent;

  // 1. DISPARAR EVENTO CLIENT-SIDE (GTM / Navegador)
  if (typeof window !== 'undefined') {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: eventName,
      eventID: eventId,
      ...customData,
    });
    console.log(`[Analytics] GTM Event: ${eventName} | EventID: ${eventId}`);

    // Atualiza os parâmetros de correspondência avançada (Advanced Matching) no Pixel do navegador antes de disparar o evento
    if (shouldSendToMeta && typeof (window as any).fbq === 'function' && userData) {
      const advancedMatching: Record<string, any> = {};
      if (userData.email) advancedMatching.em = userData.email.trim().toLowerCase();
      if (userData.phone) advancedMatching.ph = normalizePhone(userData.phone) || '';
      if (finalFirstName) advancedMatching.fn = finalFirstName.trim().toLowerCase();
      if (finalLastName) advancedMatching.ln = finalLastName.trim().toLowerCase();
      if (userData.external_id) advancedMatching.external_id = userData.external_id;

      if (Object.keys(advancedMatching).length > 0) {
        (window as any).fbq('init', '1567736884795449', advancedMatching);
      }
    }

    // Dispara diretamente no Meta Pixel (client-side) para o Meta Pixel Helper detectar instantaneamente
    if (shouldSendToMeta && typeof (window as any).fbq === 'function') {
      (window as any).fbq('track', eventName, customData, { eventID: eventId });
      console.log(`[Analytics] Meta Pixel Event: ${eventName} | EventID: ${eventId}`);
    } else if (!shouldSendToMeta) {
      console.log(`[Analytics] Meta Pixel Event: ${eventName} ignorado para a rota: ${currentRoute}`);
    }
  }

  // Se o evento não for qualificado para a Meta, encerra aqui sem chamar o servidor (CAPI)
  if (!shouldSendToMeta) {
    return Promise.resolve();
  }

  // 2. DISPARAR EVENTO SERVER-SIDE (Meta Conversions API)
  try {
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    const test_event_code = getQueryParam('test_event_code');

    const apiPayload = {
      event_name: eventName,
      event_id: eventId,
      event_source_url: typeof window !== 'undefined' ? window.location.href : '',
      ...(test_event_code && { test_event_code }),
      user_data: {
        fbp,
        fbc,
        ...(userData?.email && { email: userData.email }),
        ...(userData?.phone && { phone: normalizePhone(userData.phone) || undefined }),
        ...(finalFirstName && { first_name: finalFirstName }),
        ...(finalLastName && { last_name: finalLastName }),
        ...(userData?.external_id && { external_id: userData.external_id }),
      },
      ...(customData && { custom_data: customData }),
    };

    // Aponta para a Cloud Function do Firebase (CAPI Proxy)
    const capiUrl = '/api/trackMetaEvent';

    // Fazer a chamada HTTP assíncrona retornando a Promise para permitir aguardar se necessário
    // keepalive: true garante que a requisição seja concluída mesmo se o usuário recarregar a página
    return fetch(capiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiPayload),
      mode: 'cors',
      keepalive: true,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errText = await response.text();
          console.warn(`[AnalyticsCAPI] Resposta com erro da API Meta CAPI:`, errText);
        } else {
          console.log(`[AnalyticsCAPI] Sucesso ao disparar CAPI para ${eventName}`);
        }
      })
      .catch((err) => {
        console.warn(`[AnalyticsCAPI] Erro ao enviar chamada CAPI:`, err);
      });
  } catch (error) {
    console.warn(`[AnalyticsCAPI] Falha ao processar evento CAPI:`, error);
  }
}

/**
 * Dispara um evento puramente client-side para GTM/Google Analytics e Meta Pixel
 * sem passar pela API de Conversões do Servidor (ideal para cliques de navegação e CTAs)
 */
export function trackClientEvent(eventName: string, customData?: Record<string, any>) {
  if (typeof window !== 'undefined') {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: eventName,
      ...customData,
    });
    console.log(`[AnalyticsClient] Evento GTM disparado: ${eventName}`, customData);

    const currentRoute = getHashRoute();
    const isMetaAllowed = ['/', '/login'].includes(currentRoute);

    if (isMetaAllowed && typeof (window as any).fbq === 'function') {
      (window as any).fbq('trackCustom', eventName, customData);
      
      // Se for um clique de CTA na Landing Page, dispara também o evento padrão 'Subscribe' para otimização da Meta
      if (eventName === 'clique_cta') {
        (window as any).fbq('track', 'Subscribe', customData);
        console.log(`[AnalyticsClient] Meta Pixel Standard Event: Subscribe`, customData);
      }
    } else if (!isMetaAllowed) {
      console.log(`[AnalyticsClient] Evento Meta Pixel ignorado para a rota: ${currentRoute}`);
    }
  }
}
